import { REQUIRE_AUTH } from './auth/config';
import type { AuthSession } from './auth/types';
import { useGoogleAuth } from './auth/useGoogleAuth';
import { AppShell } from './components/layout/AppShell';
import type { ShellUser } from './components/layout/UserBadge';
import { LoginScreen } from './components/LoginScreen';
import { useApiEndpoints } from './hooks/useApiEndpoints';
import { useDashboard } from './hooks/useDashboard';
import { useNotifications } from './hooks/useNotifications';
import { useRoute } from './hooks/useRoute';
import { useSessionUser } from './hooks/useSessionUser';
import { AccountsScreen } from './screens/accounts/AccountsScreen';
import { MastersScreen } from './screens/masters/MastersScreen';
import { ApisScreen } from './screens/apis/ApisScreen';
import { AuditScreen } from './screens/audit/AuditScreen';
import { DashboardScreen } from './screens/dashboard/DashboardScreen';
import { JobsScreen } from './screens/jobs/JobsScreen';
import { LinesScreen } from './screens/lines/LinesScreen';
import { NotificationsScreen } from './screens/notifications/NotificationsScreen';
import { ProfileScreen } from './screens/profile/ProfileScreen';
import { ReportsScreen } from './screens/reports/ReportsScreen';
import { StatusScreen } from './screens/status/StatusScreen';

/**
 * Usuário mostrado no shell quando o login está desligado — o que só é possível
 * no `npm run dev` com `VITE_REQUIRE_AUTH=false` (ver `REQUIRE_AUTH`).
 */
const GUEST: ShellUser = {
  name: 'Visitante',
  email: '',
  role: 'Acesso sem login',
};

/**
 * Portão de login. **Nada que busca dados monta antes dele.**
 *
 * Os hooks de dados (painel, APIs, notificações) disparam requisições assim que
 * montam. Enquanto viviam aqui, rodavam por trás da tela de login: ela cobria a
 * interface, mas o navegador já tinha recebido os dados — visíveis na aba
 * Network a quem nunca entrou. Por isso eles ficam em `AuthenticatedApp`, que
 * só é montado depois do login, e desmontado (com os intervalos) no logout ou
 * quando a sessão expira.
 */
export default function App() {
  const { status, session, error, configError, signIn, signOut } = useGoogleAuth();

  const authenticated = status === 'authenticated' && session !== null;

  if (REQUIRE_AUTH && !authenticated) {
    return (
      <LoginScreen
        status={status}
        error={error}
        configError={configError}
        onSignIn={signIn}
      />
    );
  }

  return (
    <AuthenticatedApp
      session={authenticated ? session : null}
      onSignOut={authenticated ? signOut : undefined}
    />
  );
}

interface AuthenticatedAppProps {
  /** Nulo só com o login desligado (dev). */
  session: AuthSession | null;
  onSignOut?: () => void;
}

function AuthenticatedApp({ session, onSignOut }: AuthenticatedAppProps) {
  const { screen, navigate } = useRoute();

  // O cabeçalho mostra a saúde geral, então o estado das APIs vive aqui em cima.
  const apiEndpoints = useApiEndpoints();
  const dashboard = useDashboard();
  // O sino fica no cabeçalho de toda tela, então a central vive aqui em cima.
  const notifications = useNotifications();
  // Grava quem entrou e devolve a role do SICOP, que é o que o shell mostra.
  const sessionUser = useSessionUser(session);

  const user: ShellUser =
    session
      ? {
          name: session.user.name,
          email: session.user.email,
          // A foto é a da conta Google; sem ela o avatar cai nas iniciais.
          picture: session.user.picture ?? sessionUser?.pictureUrl ?? null,
          // Hoje o cadastro dá `admin` a todo mundo; o rótulo só muda quando
          // ele disser outra coisa, inclusive enquanto a chamada não volta.
          role: sessionUser?.role === 'viewer' ? 'Leitura' : 'Administrador',
        }
      : GUEST;

  return (
    <AppShell
      screen={screen}
      onNavigate={navigate}
      user={user}
      summary={apiEndpoints.state?.summary ?? null}
      apis={apiEndpoints.state?.apis ?? []}
      notifications={notifications}
      onSignOut={onSignOut}
    >
      {screen === 'dashboard' ? (
        <DashboardScreen controller={dashboard} apis={apiEndpoints} onNavigate={navigate} />
      ) : screen === 'status' ? (
        <StatusScreen controller={apiEndpoints} />
      ) : screen === 'apis' ? (
        <ApisScreen controller={apiEndpoints} />
      ) : screen === 'lines' ? (
        <LinesScreen onNotified={notifications.reload} />
      ) : screen === 'accounts' ? (
        <AccountsScreen />
      ) : screen === 'masters' ? (
        <MastersScreen />
      ) : screen === 'jobs' ? (
        <JobsScreen onNotified={notifications.reload} />
      ) : screen === 'notifications' ? (
        <NotificationsScreen controller={notifications} />
      ) : screen === 'audit' ? (
        <AuditScreen />
      ) : screen === 'reports' ? (
        <ReportsScreen userEmail={session ? session.user.email : null} />
      ) : (
        // Última da cadeia porque é a última tela do design que faltava: com o
        // perfil de pé, todo `ScreenId` tem tela e não sobra caso para um
        // "ainda não implementada".
        <ProfileScreen
          user={user}
          sessionUser={sessionUser}
          onSignOut={onSignOut}
        />
      )}
    </AppShell>
  );
}
